import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Award,
  BookOpen,
  GraduationCap,
  Heart,
  Link2,
  MapPin,
  Plane,
  Scissors,
  Sprout,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { MediaPlaceholder } from '../../../components/MediaPlaceholder';
import { lnAssets } from '../utils/lnAssets';
import {
  SITE_LMFT_LICENSE_YEAR,
  SITE_MFT_DEGREE,
  SITE_MFT_GRADUATION_YEAR,
  SITE_MFT_SCHOOL,
  SITE_PRACTICE_LOCATION,
  SITE_PRACTITIONER_CREDENTIALS,
  SITE_PRACTITIONER_NAME,
  SITE_SALON_YEARS,
  SITE_SUPERVISOR_LICENSE,
  SITE_SUPERVISOR_NAME,
} from '../sitePlaceholders';
import { useRevealSections } from '../hooks/useReveal';
import { BOOK_SESSION_PATH } from '../bookingConfig';
import '../styles/I3Page.css';
import '../styles/I3About.css';
import '../styles/I3AboutLayout.css';

const APPROACH = [
  {
    title: 'Connection',
    line: 'Healthy relationships begin with understanding.',
    icon: Link2,
  },
  {
    title: 'Compassion',
    line: 'Everyone deserves empathy.',
    icon: Heart,
  },
  {
    title: 'Growth',
    line: "Healing isn't perfection. It's progress.",
    icon: Sprout,
  },
  {
    title: 'Practical Tools',
    line: 'Therapy should work beyond the office.',
    icon: Wrench,
  },
] as const;

const WHO_I_HELP = [
  {
    title: 'Couples & Marriage Counseling',
    line: 'Rebuilding trust and improving communication.',
    photo: lnAssets.photos.whoHelpCouples,
    alt: 'Couple sitting together with foreheads touching in a warm living room',
    fallbackLabel: 'Couples & marriage counseling photo',
  },
  {
    title: 'Individual Therapy',
    line: 'Navigating anxiety, depression, and personal growth.',
    photo: lnAssets.photos.whoHelpIndividual,
    alt: 'Woman sitting thoughtfully in a cozy armchair with a mug',
    fallbackLabel: 'Individual therapy photo',
  },
  {
    title: 'Family Counseling',
    line: 'Resolving conflict and strengthening family bonds.',
    photo: lnAssets.photos.whoHelpFamily,
    alt: 'Happy family of four smiling together on a couch',
    fallbackLabel: 'Family counseling photo',
  },
  {
    title: 'Workshops & Groups',
    line: 'Educating communities on mental health.',
    photo: lnAssets.photos.whoHelpWorkshops,
    alt: 'Diverse group seated in a circle during a therapy workshop',
    fallbackLabel: 'Workshops & groups photo',
  },
] as const;

const LICENSE_DETAIL = [
  SITE_LMFT_LICENSE_YEAR ? `Licensed ${SITE_LMFT_LICENSE_YEAR}` : null,
  `Supervised by ${SITE_SUPERVISOR_NAME} # ${SITE_SUPERVISOR_LICENSE}`,
]
  .filter(Boolean)
  .join(' · ');

const CREDENTIALS: {
  icon: LucideIcon;
  title: string;
  line: string;
  subline?: string;
}[] = [
  {
    icon: Award,
    title: 'Texas LMFT Associate',
    line: 'Licensed Marriage & Family Therapist Associate',
    subline: LICENSE_DETAIL,
  },
  {
    icon: GraduationCap,
    title: SITE_MFT_DEGREE,
    line: SITE_MFT_SCHOOL,
    subline: `Graduated ${SITE_MFT_GRADUATION_YEAR}`,
  },
  {
    icon: Scissors,
    title: 'A Lifetime of Listening',
    line: `${SITE_SALON_YEARS} years in the salon chair before becoming a therapist`,
  },
  {
    icon: MapPin,
    title: 'Telehealth & In-Person',
    line: `${SITE_PRACTICE_LOCATION} · Secure video or office sessions`,
  },
];

const BEYOND_COLLAGE = [
  {
    src: lnAssets.photos.beyondTravel1,
    alt: 'Eiffel Tower at sunset, Paris',
    fallbackLabel: 'Eiffel Tower — Paris',
  },
  {
    src: lnAssets.photos.beyondTravel2,
    alt: 'Roman Colosseum at sunset',
    fallbackLabel: 'Colosseum — Rome',
  },
  {
    src: lnAssets.photos.beyondTravel3,
    alt: 'Hong Kong skyline at sunset with traditional junk boat',
    fallbackLabel: 'Hong Kong harbour',
  },
  {
    src: lnAssets.photos.beyondTravel4,
    alt: 'Sydney Opera House at sunset',
    fallbackLabel: 'Sydney Opera House',
  },
] as const;

type AboutPhotoProps = {
  src: string;
  alt: string;
  className?: string;
  fallbackLabel: string;
};

const AboutPhoto: React.FC<AboutPhotoProps> = ({ src, alt, className = '', fallbackLabel }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return <MediaPlaceholder className={className} label={fallbackLabel} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
};

const I3About: React.FC = () => {
  const articleRef = useRef<HTMLElement>(null);
  useRevealSections(articleRef);

  return (
    <article className="i3-about" ref={articleRef}>
      <h1 className="i3-about-sr-only">About {SITE_PRACTITIONER_NAME}</h1>

      {/* BATCH 1 — Hero (slate background — no beach video; that’s home only) */}
      <section className="i3-about-hero" aria-labelledby="i3-about-hero-title">
        <div className="i3-about-hero-inner">
          <div className="i3-about-photo-slot i3-about-hero-photo-wrap">
            <AboutPhoto
              src={lnAssets.photos.lisaAboutHero}
              alt={SITE_PRACTITIONER_CREDENTIALS}
              className="i3-about-hero-photo"
              fallbackLabel={`${SITE_PRACTITIONER_NAME} — warm professional portrait (office)`}
            />
          </div>
          <div className="i3-about-hero-copy">
            <p className="i3-about-hero-sub">{SITE_PRACTITIONER_CREDENTIALS}</p>
            <h2 id="i3-about-hero-title" className="i3-about-hero-headline">
              Helping Families Find Their Way Back to Connection
            </h2>
            <p className="i3-about-hero-intro">
              After {SITE_SALON_YEARS} years listening to people&apos;s stories behind the salon chair, I&apos;m
              now a licensed therapist — helping couples, families, and individuals navigate life&apos;s
              challenges and find connection again.
            </p>
            <p className="i3-about-hero-tagline">You don&apos;t have to walk this journey alone.</p>
            <div className="i3-about-hero-actions">
              <Link to={BOOK_SESSION_PATH} className="i3-bridge-btn i3-bridge-btn--sand">
                Book a Session
              </Link>
              <Link to="/contact" className="i3-bridge-btn i3-bridge-btn--sand">
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="i3-page-wrap i3-about-page">
        {/* BATCH 3 — Therapy changed my life (photo left, copy right) */}
        <section
          className="i3-about-section i3-about-relatable i3-reveal"
          aria-labelledby="i3-about-relatable-title"
        >
          <div className="i3-about-split">
            <div className="i3-about-split-media">
              <div className="i3-about-photo-slot i3-about-relatable-photo-wrap">
                <AboutPhoto
                  src={lnAssets.photos.lisaCouple}
                  alt={`${SITE_PRACTITIONER_NAME} with her husband`}
                  className="i3-about-relatable-photo"
                  fallbackLabel="Lisa with her husband — couple photo"
                />
              </div>
            </div>
            <div className="i3-about-split-copy">
              <h2 id="i3-about-relatable-title" className="i3-about-split-heading">
                Therapy Changed My Life Too
              </h2>
              <div className="i3-about-prose i3-about-prose--left">
                <p>
                  Counseling helped strengthen our own marriage and gave us tools that still serve us
                  today.
                </p>
                <p>
                  Because of that experience, I know what it&apos;s like to sit on both sides of the
                  therapy room.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* BATCH 4 — My approach (4 columns) */}
        <section
          className="i3-about-section i3-about-section--band i3-about-approach i3-reveal"
          aria-labelledby="i3-about-approach-title"
        >
          <h2 id="i3-about-approach-title" className="i3-about-section-heading">
            My Approach
          </h2>
          <ul className="i3-about-approach-grid">
            {APPROACH.map(({ title, line, icon: Icon }) => (
              <li key={title} className="i3-about-approach-item">
                <span className="i3-about-approach-icon" aria-hidden>
                  <Icon size={26} strokeWidth={1.5} />
                </span>
                <h3>{title}</h3>
                <p>{line}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* BATCH 5 — Why The Bridge (copy left, scenic photo right) */}
        <section
          className="i3-about-section i3-about-bridge i3-reveal"
          aria-labelledby="i3-about-bridge-title"
        >
          <div className="i3-about-bridge-split">
            <div className="i3-about-bridge-copy">
              <h2 id="i3-about-bridge-title" className="i3-about-split-heading i3-about-split-heading--left">
                Why The Bridge?
              </h2>
              <div className="i3-about-prose i3-about-prose--left">
                <p className="i3-about-bridge-lead">
                  A bridge connects where we are with where we hope to be.
                </p>
                <p>
                  Many people arrive feeling stuck, overwhelmed, or disconnected.
                </p>
                <p>Therapy helps build a path forward.</p>
                <p className="i3-about-bridge-closer">You don&apos;t have to cross it alone.</p>
              </div>
            </div>
            <div className="i3-about-bridge-photo-slot i3-about-photo-slot">
              <AboutPhoto
                src={lnAssets.photos.bridgeScenic}
                alt="Scenic bridge at sunset"
                className="i3-about-bridge-photo"
                fallbackLabel="Scenic bridge photo — sunset / water"
              />
            </div>
          </div>
        </section>

        {/* BATCH 6 — Who I help */}
        <section
          className="i3-about-section i3-about-section--band i3-about-who i3-reveal"
          aria-labelledby="i3-about-who-title"
        >
          <h2 id="i3-about-who-title" className="i3-about-section-heading">
            Who I Help
          </h2>
          <ul className="i3-about-who-grid">
            {WHO_I_HELP.map(({ title, line, photo, alt, fallbackLabel }) => (
              <li key={title} className="i3-about-who-card">
                <div className="i3-about-who-photo-wrap i3-about-photo-slot">
                  <AboutPhoto
                    src={photo}
                    alt={alt}
                    className="i3-about-who-photo"
                    fallbackLabel={fallbackLabel}
                  />
                </div>
                <div className="i3-about-who-card-body">
                  <h3>{title}</h3>
                  <p>{line}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* BATCH 7 — Beyond the office (copy left, collage right) */}
        <section className="i3-about-section i3-about-beyond i3-reveal" aria-labelledby="i3-about-beyond-title">
          <div className="i3-about-beyond-split">
            <div className="i3-about-beyond-copy">
              <h2 id="i3-about-beyond-title" className="i3-about-split-heading i3-about-split-heading--left">
                Beyond the Office
              </h2>
              <div className="i3-about-prose i3-about-prose--left i3-about-beyond-facts">
                <p>Born in Michigan. {SITE_PRACTICE_LOCATION} resident.</p>
                <p>Married 33+ years. Mother of 3 sons. Grandmother of 3.</p>
                <p>
                  Loves reading, traveling, family, and helping people find connection.
                </p>
              </div>
              <ul className="i3-about-beyond-tags" aria-label="Life interests">
                <li>
                  <Plane size={16} strokeWidth={1.75} aria-hidden /> Travel
                </li>
                <li>
                  <Users size={16} strokeWidth={1.75} aria-hidden /> Family
                </li>
                <li>
                  <BookOpen size={16} strokeWidth={1.75} aria-hidden /> Books
                </li>
                <li>
                  <Scissors size={16} strokeWidth={1.75} aria-hidden /> Hair Styling
                </li>
              </ul>
            </div>
            <div className="i3-about-beyond-collage" aria-label="Travel landmarks">
              {BEYOND_COLLAGE.map(({ src, alt, fallbackLabel }) => (
                <div key={alt} className="i3-about-beyond-tile i3-about-photo-slot">
                  <AboutPhoto
                    src={src}
                    alt={alt}
                    className="i3-about-beyond-tile-photo"
                    fallbackLabel={fallbackLabel}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BATCH 8 — Credentials */}
        <section
          className="i3-about-section i3-about-section--band i3-about-credentials i3-reveal"
          aria-labelledby="i3-about-cred-title"
        >
          <h2 id="i3-about-cred-title" className="i3-about-section-heading">
            Credentials
          </h2>
          <ul className="i3-about-credentials-grid">
            {CREDENTIALS.map(({ icon: Icon, title, line, subline }) => (
              <li key={title} className="i3-about-credentials-item">
                <span className="i3-about-credentials-icon" aria-hidden>
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                <div>
                  <h3>{title}</h3>
                  <p>{line}</p>
                  {subline ? <p className="i3-about-credentials-subline">{subline}</p> : null}
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* BATCH 9 — Final CTA (full bleed, bridge graphic slot) */}
      <section
        className="i3-about-final-cta i3-reveal"
        aria-labelledby="i3-about-final-cta-title"
      >
        <div className="i3-about-final-cta-backdrop" aria-hidden>
          <AboutPhoto
            src={lnAssets.photos.aboutCtaBridge}
            alt=""
            className="i3-about-final-cta-bridge-art"
            fallbackLabel="Bridge line art / graphic — footer background"
          />
        </div>
        <div className="i3-about-final-cta-inner">
          <h2 id="i3-about-final-cta-title" className="i3-about-final-cta-title">
            Ready to Take the First Step?
          </h2>
          <p className="i3-about-final-cta-lead">
            You don&apos;t have to navigate life&apos;s challenges alone.
          </p>
          <p className="i3-about-final-cta-body">
            Together we can build a bridge toward healing, clarity, and growth.
          </p>
          <div className="i3-about-final-cta-actions">
            <Link to={BOOK_SESSION_PATH} className="i3-bridge-btn i3-bridge-btn--sand">
              Book a Session
            </Link>
            <Link to="/contact" className="i3-bridge-btn i3-bridge-btn--sand">
              Contact Lisa
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
};

export default I3About;
